import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Activity, BarChart3, ShieldCheck, Users, Wrench } from 'lucide-react'
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { AppChrome } from '../components/AppChrome'
import { useAuth } from '../lib/auth'
import { describeSync, syncAllRuns, syncPages, type SyncPage, type SyncSummary } from '../lib/squadSync'

type Signal = { level: string; code: string; detail: string }
type Tracking = {
  activity_count: number
  by_kind: Record<string, number>
  runs_touched: number
  stages_completed: number
  stages_failed: number
  failure_rate: number | null
  mean_stage_duration_s: number | null
  skill_invocations: Record<string, number>
  cost_usd: number
  tokens: number
  handoffs_sent: number
  handoffs_received: number
  open_blockers: Array<{ blocker_id: string; summary?: string | null; since?: string; run_id?: string | null }>
  open_blockers_count?: number
  awaiting_human: Array<{ run_id?: string | null; stage_id?: string | null; since?: string }>
  awaiting_human_count?: number
  last_activity_at?: string | null
  idle_days?: number | null
}
type Intelligence = {
  team_id: string
  name?: string
  tier?: string
  status?: string
  template_id?: string | null
  members: number
  skills_bound: string[]
  adherence_score?: number | null
  compliance_status?: string
  generation?: number
  qualifications: { as_subject: number; passed: number; pending: number }
  lessons: number
  tracking: Tracking
  signals: Signal[]
}
type FleetPage = { offset: number; limit: number; next_offset: number | null; total_squads: number; deferred: string[]; complete: boolean }
type Fleet = {
  page?: FleetPage
  totals: { squads: number; super_teams: number; activity_count: number; stages_completed: number; stages_failed: number; cost_usd: number; open_blockers: number }
  skill_invocations: Record<string, number>
  attention: Array<{ team_id: string; name?: string; signals: Signal[] }>
  ranking: Array<{ team_id: string; name?: string; tier?: string; stages_completed: number; failure_rate: number | null; cost_usd: number; adherence_score?: number | null }>
  squads: Intelligence[]
}
type Scorecard = {
  scorecard_id?: string
  team_id: string
  name?: string
  score: number | null
  grade: string | null
  dimensions: Record<string, { score: number | null; evidence: string }>
  promotion: { eligible: boolean; already_super_team: boolean; reasons: string[]; note: string }
  regression: { regressed: boolean; reasons: string[]; score_delta?: number }
  recorded_at?: string
  recorded_by?: string
}
type FleetScorecards = {
  page?: FleetPage
  totals: { squads: number; graded: number; grades: Record<string, number>; promotion_candidates: string[]; regressed: string[] }
  ranking: Array<{ team_id: string; name?: string; tier?: string; score: number | null; grade: string | null }>
}
type SkillProfile = {
  bound: string[]
  used: string[]
  unbound_used: string[]
  unused_bound: string[]
  missing_from_library: string[]
  coverage: number | null
  skills: Array<{ skill_key: string; bound: boolean; in_effective_library: boolean; invocations: number; stages_completed: number; stages_failed: number; success_rate: number | null }>
}
type ActivityRow = { activity_id: string; kind: string; at?: string; actor?: string; run_id?: string | null; stage_id?: string | null; summary?: string | null; cost_usd?: number | null }
type TeamsOverview = { dimensions?: { adherence?: string[]; compliance?: string[] } }
type TeamRecord = { compliance?: { checks?: Record<string, boolean> } }

// Manual kinds only: stage and gate transitions are recorded by the pipeline
// (the API answers 422 for them), so they are not offered here.
const ACTIVITY_KINDS = ['note', 'skill_invoked', 'blocker_raised', 'blocker_cleared', 'handoff_sent', 'handoff_received', 'qualification', 'lesson_recorded'] as const

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  })
  if (!res.ok) throw new Error(await res.text())
  return res.json() as Promise<T>
}

const field = 'w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3'
const card = 'space-y-4 rounded-3xl border border-[var(--color-line)] bg-black/25 p-5 sm:p-6'
const h2 = 'flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold'
const primary = 'rounded-full bg-[var(--color-cyan)] px-4 py-2 font-semibold text-[var(--color-void)] disabled:opacity-50'
const secondary = 'rounded-full border border-[var(--color-line)] px-4 py-2 font-semibold disabled:opacity-50'

function pct(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : `${Math.round(value * 100)}%`
}

export function SquadsPage() {
  const { user, loading } = useAuth()
  const [fleet, setFleet] = useState<Fleet | null>(null)
  const [cards, setCards] = useState<FleetScorecards | null>(null)
  const [teamsMeta, setTeamsMeta] = useState<TeamsOverview | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [intel, setIntel] = useState<Intelligence | null>(null)
  const [activity, setActivity] = useState<ActivityRow[]>([])
  const [scorecard, setScorecard] = useState<Scorecard | null>(null)
  const [history, setHistory] = useState<Scorecard[]>([])
  const [skills, setSkills] = useState<SkillProfile | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [syncProgress, setSyncProgress] = useState<SyncSummary | null>(null)
  // Resume cursor: set when a walk stopped at the page cap or was cancelled,
  // so the next click continues from that run instead of offset zero.
  const [syncCursor, setSyncCursor] = useState<number | null>(null)
  // Pages whose stage writes failed in any segment of the current walk. They
  // survive "Continue sync" clicks so a clean final segment can never report
  // completion while earlier failures were never retried.
  const [syncFailedPages, setSyncFailedPages] = useState<number[]>([])
  const syncAbort = useRef<AbortController | null>(null)

  const [adherence, setAdherence] = useState<Record<string, string>>({})
  const [compliance, setCompliance] = useState<Record<string, boolean>>({})
  const [bindKeys, setBindKeys] = useState('')
  const [activityKind, setActivityKind] = useState<string>('note')
  const [activitySummary, setActivitySummary] = useState('')
  const [activityBlocker, setActivityBlocker] = useState('')
  const [activitySkills, setActivitySkills] = useState('')
  const [activityRun, setActivityRun] = useState('')
  const [activityStage, setActivityStage] = useState('')
  const [activityCounterpart, setActivityCounterpart] = useState('')
  const [loadingSquad, setLoadingSquad] = useState(false)
  const [complianceLoaded, setComplianceLoaded] = useState(false)
  const adherenceDims = teamsMeta?.dimensions?.adherence || []
  const complianceChecks = teamsMeta?.dimensions?.compliance || []
  // Monotonic selection token: responses for a squad that is no longer
  // selected are discarded, so detail panels never show another squad's data.
  const selectionRef = useRef(0)
  const selectedRef = useRef<string | null>(null)

  async function loadFleet() {
    const [f, c, t] = await Promise.all([
      api<Fleet>('/api/afos360/v1/squads'),
      api<FleetScorecards>('/api/afos360/v1/squads/scorecards'),
      api<TeamsOverview>('/api/afos360/v1/teams').catch(() => ({})),
    ])
    setFleet(f)
    setCards(c)
    setTeamsMeta(t)
  }

  async function loadSquad(teamId: string) {
    const token = ++selectionRef.current
    setLoadingSquad(true)
    try {
      const [d, s, k, team] = await Promise.all([
        api<{ intelligence: Intelligence; activity: ActivityRow[] }>(`/api/afos360/v1/squads/${encodeURIComponent(teamId)}?limit=40`),
        api<{ scorecard: Scorecard; history: Scorecard[] }>(`/api/afos360/v1/squads/${encodeURIComponent(teamId)}/scorecard`),
        api<SkillProfile>(`/api/afos360/v1/squads/${encodeURIComponent(teamId)}/skills`),
        api<TeamRecord>(`/api/afos360/v1/teams/${encodeURIComponent(teamId)}`).then(
          (team): { ok: true; team: TeamRecord } => ({ ok: true, team }),
          (err: unknown): { ok: false; reason: string } => ({ ok: false, reason: err instanceof Error ? err.message : 'team record failed' }),
        ),
      ])
      if (token !== selectionRef.current) return // a newer selection won
      setIntel(d.intelligence)
      setActivity(d.activity)
      setScorecard(s.scorecard)
      setHistory(s.history)
      setSkills(k)
      // Seed the compliance form from what is persisted so an untouched
      // checkbox never silently means "unchanged". If the record cannot be
      // loaded, the audit stays disabled rather than submitting false for all.
      if (team.ok) {
        setCompliance({ ...(team.team.compliance?.checks || {}) })
        setComplianceLoaded(true)
      } else {
        setCompliance({})
        setComplianceLoaded(false)
        setError(`Compliance checks could not be loaded (${team.reason}); audit disabled`)
      }
    } finally {
      if (token === selectionRef.current) setLoadingSquad(false)
    }
  }

  useEffect(() => {
    if (user?.customer_id && user?.project_id) {
      void loadFleet().catch((err) => setError(err instanceof Error ? err.message : 'Load failed'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.customer_id, user?.project_id])

  if (!loading && !user) return <Navigate to="/signin" replace />

  async function act(label: string, fn: () => Promise<unknown>) {
    const target = selectedRef.current
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      await fn()
      setMessage(label)
      await loadFleet()
      // Only refresh the squad the action was for, and only if it is still the
      // selected one; otherwise the newer selection keeps its own data.
      if (target && selectedRef.current === target) await loadSquad(target)
    } catch (err) {
      setError(err instanceof Error ? err.message : `${label} failed`)
    } finally {
      setBusy(false)
    }
  }

  // Sync follows next_offset until the server reports the last page, so a
  // project with more than one page of runs is never reported as synced after
  // the first 200. Incomplete results and write failures are surfaced as such.
  function fetchSyncPage(controller: AbortController) {
    return (offset: number, limit: number) =>
      api<SyncPage>(`/api/afos360/v1/squads/sync?limit=${limit}&offset=${offset}`, { method: 'POST', signal: controller.signal })
  }

  async function runSync() {
    const target = selectedRef.current
    const controller = new AbortController()
    syncAbort.current = controller
    setBusy(true)
    setError(null)
    setMessage(null)
    setSyncProgress(null)
    // Track the cursor as pages complete, so even an unexpected throw keeps
    // the last completed page rather than where the walk began.
    const startOffset = syncCursor ?? 0
    let lastCompleted = startOffset
    // Failed pages are persisted as progress arrives, so a later request
    // error in the same segment can never discard them.
    let segmentFailed: number[] = []
    const mergeFailed = (pages: number[]) => Array.from(new Set([...syncFailedPages, ...pages])).sort((a, b) => a - b)
    try {
      const summary = await syncAllRuns(fetchSyncPage(controller), {
        signal: controller.signal,
        startOffset,
        onProgress: (progress) => {
          lastCompleted = startOffset + progress.runs_scanned
          segmentFailed = progress.failed_pages
          setSyncFailedPages(mergeFailed(progress.failed_pages))
          setSyncProgress(progress)
        },
      })
      const failedPages = mergeFailed(summary.failed_pages)
      setSyncFailedPages(failedPages)
      setSyncCursor(summary.next_offset)
      const reachedEnd = summary.next_offset === null && summary.stopped !== 'stalled'
      if (reachedEnd && failedPages.length === 0) {
        setMessage(describeSync(summary))
      } else if (reachedEnd) {
        setError(
          `Sync visited every run but ${failedPages.length} page(s) had stage write failures in this or an earlier segment; use "Retry failed pages" before trusting the metrics` +
            (summary.write_failures.length ? ` — latest: ${summary.write_failures[0].run_id}/${summary.write_failures[0].stage_id}: ${summary.write_failures[0].error}` : ''),
        )
      } else {
        setError(describeSync(summary) + (failedPages.length ? ` — ${failedPages.length} page(s) with write failures are retained for retry` : ''))
      }
      await loadFleet()
      if (target && selectedRef.current === target) await loadSquad(target)
    } catch (err) {
      const retained = mergeFailed(segmentFailed)
      setSyncFailedPages(retained)
      setSyncCursor(lastCompleted)
      const kept = retained.length ? ` ${retained.length} page(s) with write failures are retained for retry.` : ''
      if (controller.signal.aborted) {
        setError(`Sync cancelled at run ${lastCompleted}; squad metrics may be incomplete. Click "Continue sync" to resume from there.${kept}`)
      } else {
        setError(`${err instanceof Error ? err.message : 'Sync failed'} (stopped at run ${lastCompleted}; click "Continue sync" to resume).${kept}`)
      }
    } finally {
      syncAbort.current = null
      setSyncProgress(null)
      setBusy(false)
    }
  }

  async function retryFailedPages() {
    if (syncFailedPages.length === 0) return
    const target = selectedRef.current
    const controller = new AbortController()
    syncAbort.current = controller
    setBusy(true)
    setError(null)
    setMessage(null)
    // Repaired pages are dropped from the retained list as progress arrives,
    // so a later request error (or the sync quota) can never make the next
    // click retry pages that were already fixed; failed and unvisited pages stay.
    const original = [...syncFailedPages]
    let remaining = original
    try {
      const summary = await syncPages(fetchSyncPage(controller), original, {
        signal: controller.signal,
        onProgress: (progress) => {
          remaining = original.filter((o) => !progress.repaired_pages.includes(o))
          setSyncFailedPages(remaining)
          setSyncProgress(progress)
        },
      })
      remaining = original.filter((o) => !summary.repaired_pages.includes(o))
      setSyncFailedPages(remaining)
      if (remaining.length === 0) setMessage(`Retried ${summary.pages} page(s): ${summary.recorded} recorded, ${summary.unchanged} unchanged; no write failures remain`)
      else setError(`${remaining.length} page(s) still have write failures` + (summary.write_failures.length ? ` — ${summary.write_failures[0].run_id}/${summary.write_failures[0].stage_id}: ${summary.write_failures[0].error}` : ''))
      await loadFleet()
      if (target && selectedRef.current === target) await loadSquad(target)
    } catch (err) {
      setSyncFailedPages(remaining)
      setError(`${err instanceof Error ? err.message : 'Retry failed'}; ${remaining.length} page(s) remain retained for retry`)
    } finally {
      syncAbort.current = null
      setSyncProgress(null)
      setBusy(false)
    }
  }

  async function onSelect(teamId: string) {
    if (busy) return // no selection changes while a write is in flight
    setError(null)
    setSelected(teamId)
    selectedRef.current = teamId
    // Clear the previous squad's panels immediately so actions can never
    // target the new squad while the old squad's data is still displayed.
    setIntel(null)
    setActivity([])
    setScorecard(null)
    setHistory([])
    setSkills(null)
    setAdherence({})
    setCompliance({})
    setComplianceLoaded(false)
    setBindKeys('')
    setActivitySummary('')
    setActivityBlocker('')
    setActivitySkills('')
    setActivityRun('')
    setActivityCounterpart('')
    setActivityStage('')
    try {
      await loadSquad(teamId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Load failed')
    }
  }

  function onAdherence(e: FormEvent) {
    e.preventDefault()
    if (!selected) return
    const dims: Record<string, number> = {}
    for (const [k, v] of Object.entries(adherence)) if (v.trim()) dims[k] = Number(v)
    void act('Adherence scored', () =>
      api(`/api/afos360/v1/teams/${encodeURIComponent(selected)}/adherence`, { method: 'POST', body: JSON.stringify({ dimensions: dims }) }),
    )
  }

  function onCompliance(e: FormEvent) {
    e.preventDefault()
    if (!selected || !complianceLoaded) return
    // Submit an explicit boolean for every displayed check.
    const checks: Record<string, boolean> = {}
    for (const c of complianceChecks) checks[c] = !!compliance[c]
    void act('Compliance audited', () =>
      api(`/api/afos360/v1/teams/${encodeURIComponent(selected)}/compliance`, { method: 'POST', body: JSON.stringify({ checks }) }),
    )
  }

  function onBind(e: FormEvent) {
    e.preventDefault()
    if (!selected) return
    const keys = bindKeys.split(',').map((s) => s.trim()).filter(Boolean)
    void act('Skills bound', () =>
      api(`/api/afos360/v1/teams/${encodeURIComponent(selected)}/skills`, { method: 'POST', body: JSON.stringify({ skill_keys: keys }) }),
    )
  }

  const needsSkills = activityKind === 'skill_invoked'
  const needsBlocker = activityKind === 'blocker_raised' || activityKind === 'blocker_cleared'
  const needsCounterpart = activityKind === 'handoff_sent' || activityKind === 'handoff_received'
  const skillList = activitySkills.split(',').map((s) => s.trim()).filter(Boolean)
  const activityReady =
    (!needsSkills || skillList.length > 0) && (!needsBlocker || activityBlocker.trim()) && (!needsCounterpart || activityCounterpart.trim())

  function onActivity(e: FormEvent) {
    e.preventDefault()
    if (!selected || !activityReady) return
    void act('Activity recorded', () =>
      api(`/api/afos360/v1/squads/${encodeURIComponent(selected)}/activity`, {
        method: 'POST',
        body: JSON.stringify({
          kind: activityKind,
          summary: activitySummary || null,
          blocker_id: needsBlocker ? activityBlocker.trim() : null,
          skill_keys: needsSkills ? skillList : [],
          run_id: activityRun.trim() || null,
          stage_id: activityStage.trim() || null,
          counterpart_team_id: needsCounterpart ? activityCounterpart.trim() : null,
        }),
      }),
    )
  }

  const chartData = Object.entries(fleet?.skill_invocations || {})
    .slice(0, 10)
    .map(([skill, count]) => ({ skill, count }))
  const gradeByTeam = new Map((cards?.ranking || []).map((r) => [r.team_id, r]))

  return (
    <AppChrome>
      <main className="afos-page grid gap-6 lg:grid-cols-3">
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="lg:col-span-3">
          <h1 className="afos-page-title">Squads</h1>
          <p className="afos-page-lead">
            Management, reporting, analytics, and admin for every agent squad: what each squad worked, what it
            cost, where it is blocked or waiting on a human, how its skills perform, and how it scores. Scoring
            is advisory; promotion stays a named-human action.
          </p>
          {message && <p className="mt-3 text-sm text-[var(--color-cyan)]">{message}</p>}
          {error && <p className="mt-3 text-sm text-[var(--color-rose)]">{error}</p>}
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" disabled={busy} className={secondary} onClick={() => void runSync()}>
              {syncCursor !== null ? `Continue sync (from run ${syncCursor})` : 'Sync from runs'}
            </button>
            {syncFailedPages.length > 0 && (
              <button type="button" disabled={busy} className={secondary} onClick={() => void retryFailedPages()}>
                Retry failed pages ({syncFailedPages.length})
              </button>
            )}
            {(syncCursor !== null || syncFailedPages.length > 0) && !busy && (
              <button type="button" className={secondary} onClick={() => { setSyncCursor(null); setSyncFailedPages([]) }}>
                Restart sync from the beginning
              </button>
            )}
            {syncProgress && (
              <>
                <span className="self-center text-sm text-[var(--color-muted)]">
                  Syncing… page {syncProgress.pages}, {syncProgress.runs_scanned} of {syncProgress.runs_total} runs
                  {syncProgress.write_failure_count ? `, ${syncProgress.write_failure_count} write failure(s)` : ''}
                </span>
                <button type="button" className={secondary} onClick={() => syncAbort.current?.abort()}>
                  Cancel sync
                </button>
              </>
            )}
            <button type="button" disabled={busy} className={secondary} onClick={() => void act('Default TEAM templates bootstrapped', () => api('/api/afos360/v1/teams/bootstrap', { method: 'POST' }))}>
              Bootstrap squads
            </button>
          </div>
        </motion.section>

        <section className={card}>
          <h2 className={h2}>
            <Users className="h-5 w-5 text-[var(--color-amber)]" />
            Fleet
          </h2>
          {cards?.page && cards.page.complete === false && (
            <p className="text-sm text-[var(--color-amber)]">
              Partial scorecard coverage: grades, regressions, and promotion candidates below cover {cards.totals.squads} of{' '}
              {cards.page.total_squads} squads ({cards.page.deferred.length} deferred by the per-request work budget
              {cards.page.next_offset !== null ? ', more beyond this page' : ''}); reload to score the rest.
            </p>
          )}
          {fleet?.page && fleet.page.complete === false && (
            <p className="text-sm text-[var(--color-amber)]">
              Partial fleet view: {fleet.page.deferred.length} squad(s) deferred by the per-request work budget
              {fleet.page.next_offset !== null ? `, more squads beyond this page` : ''}. Totals cover {fleet.totals.squads} of{' '}
              {fleet.page.total_squads}; reload to fold the rest.
            </p>
          )}
          {fleet && (
            <div className="grid grid-cols-2 gap-2 text-sm">
              {[
                ['Squads', fleet.totals.squads],
                ['Super teams', fleet.totals.super_teams],
                ['Stages done', fleet.totals.stages_completed],
                ['Stages failed', fleet.totals.stages_failed],
                ['Cost $', fleet.totals.cost_usd.toFixed(2)],
                ['Open blockers', fleet.totals.open_blockers],
              ].map(([label, value]) => (
                <div key={String(label)} className="rounded-2xl border border-[var(--color-line)] px-3 py-2">
                  <div className="text-xs text-[var(--color-mist)]">{label}</div>
                  <div className="text-lg font-semibold">{value}</div>
                </div>
              ))}
            </div>
          )}
          {cards && (
            <div className="text-xs text-[var(--color-mist)]">
              Grades: {Object.entries(cards.totals.grades).map(([g, n]) => `${g}×${n}`).join(' · ') || 'none yet'} · promotion
              candidates: {cards.totals.promotion_candidates.length} · regressed: {cards.totals.regressed.length}
            </div>
          )}
          {fleet && fleet.attention.length > 0 && (
            <div className="space-y-1 text-sm">
              <div className="font-semibold text-[var(--color-rose)]">Attention</div>
              {fleet.attention.map((a) => (
                <button key={a.team_id} type="button" className="block w-full text-left" onClick={() => void onSelect(a.team_id)}>
                  {a.name || a.team_id}: {a.signals.map((s) => s.detail).join('; ')}
                </button>
              ))}
            </div>
          )}
          <div className="space-y-1 text-sm">
            <div className="font-semibold">Ranking</div>
            {(fleet?.ranking || []).map((r) => {
              const g = gradeByTeam.get(r.team_id)
              return (
                <button
                  key={r.team_id}
                  type="button"
                  onClick={() => void onSelect(r.team_id)}
                  className={`flex w-full items-center justify-between rounded-2xl border px-3 py-2 text-left ${
                    selected === r.team_id ? 'border-[var(--color-cyan)]' : 'border-[var(--color-line)]'
                  }`}
                >
                  <span>
                    {r.name || r.team_id} <span className="text-[var(--color-mist)]">· {r.tier}</span>
                  </span>
                  <span className="text-[var(--color-mist)]">
                    {g?.grade || '—'} · {r.stages_completed} done · fail {pct(r.failure_rate)} · ${r.cost_usd.toFixed(2)}
                  </span>
                </button>
              )
            })}
          </div>
        </section>

        <section className={`${card} lg:col-span-2`}>
          <h2 className={h2}>
            <BarChart3 className="h-5 w-5 text-[var(--color-cyan)]" />
            Analytics · skill invocations (fleet)
          </h2>
          {chartData.length === 0 ? (
            <p className="text-sm text-[var(--color-mist)]">No skill activity yet. Run a ticket through the SDLC or record activity.</p>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <XAxis dataKey="skill" stroke="var(--color-mist)" fontSize={11} />
                  <YAxis stroke="var(--color-mist)" fontSize={11} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" fill="var(--color-cyan)" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {selected && loadingSquad && <p className="text-sm text-[var(--color-mist)]">Loading {selected}…</p>}
          {intel && scorecard && (
            <div className="grid gap-4 text-sm md:grid-cols-2">
              <div className="space-y-1 rounded-2xl border border-[var(--color-line)] p-3">
                <div className="font-semibold">
                  {intel.name || intel.team_id} · {intel.tier} · gen {intel.generation}
                </div>
                <div>Members {intel.members} · bound skills {intel.skills_bound.length} · lessons {intel.lessons}</div>
                <div>
                  Adherence {intel.adherence_score ?? '—'} · compliance {intel.compliance_status} · quals {intel.qualifications.passed}/
                  {intel.qualifications.as_subject} ({intel.qualifications.pending} pending)
                </div>
                <div>
                  Runs {intel.tracking.runs_touched} · done {intel.tracking.stages_completed} · failed {intel.tracking.stages_failed} · fail{' '}
                  {pct(intel.tracking.failure_rate)} · mean {intel.tracking.mean_stage_duration_s ?? '—'}s
                </div>
                <div>
                  Cost ${intel.tracking.cost_usd.toFixed(2)} · tokens {intel.tracking.tokens} · handoffs {intel.tracking.handoffs_sent}/
                  {intel.tracking.handoffs_received} · idle {intel.tracking.idle_days ?? '—'}d
                </div>
                <div>
                  Open blockers ({intel.tracking.open_blockers_count ?? intel.tracking.open_blockers.length}
                  {(intel.tracking.open_blockers_count ?? 0) > intel.tracking.open_blockers.length ? `, newest ${intel.tracking.open_blockers.length} shown` : ''}):{' '}
                  {intel.tracking.open_blockers.map((b) => b.blocker_id).join(', ') || 'none'}
                </div>
                <div>
                  Waiting on human ({intel.tracking.awaiting_human_count ?? intel.tracking.awaiting_human.length}
                  {(intel.tracking.awaiting_human_count ?? 0) > intel.tracking.awaiting_human.length ? `, newest ${intel.tracking.awaiting_human.length} shown` : ''}):{' '}
                  {intel.tracking.awaiting_human.map((w) => `${w.run_id}/${w.stage_id}`).join(', ') || 'none'}
                </div>
                {intel.signals.length > 0 && (
                  <div className="text-[var(--color-amber)]">Signals: {intel.signals.map((s) => `${s.code} (${s.detail})`).join('; ')}</div>
                )}
              </div>
              <div className="space-y-1 rounded-2xl border border-[var(--color-line)] p-3">
                <div className="flex items-center justify-between font-semibold">
                  <span>Scorecard</span>
                  <span>
                    {scorecard.grade || '—'} · {scorecard.score ?? '—'}
                    {scorecard.regression.regressed ? ' · REGRESSED' : ''}
                  </span>
                </div>
                {Object.entries(scorecard.dimensions).map(([k, d]) => (
                  <div key={k} className="flex justify-between gap-2">
                    <span>{k}</span>
                    <span className="text-right text-[var(--color-mist)]">
                      {d.score ?? '—'} · {d.evidence}
                    </span>
                  </div>
                ))}
                <div className={scorecard.promotion.eligible ? 'text-[var(--color-cyan)]' : 'text-[var(--color-mist)]'}>
                  Promotion: {scorecard.promotion.already_super_team ? 'already super team' : scorecard.promotion.eligible ? 'eligible (human promotes)' : scorecard.promotion.reasons.join('; ')}
                </div>
                {scorecard.regression.regressed && <div className="text-[var(--color-rose)]">{scorecard.regression.reasons.join('; ')}</div>}
                <div className="flex flex-wrap gap-2 pt-1">
                  <button type="button" disabled={busy || loadingSquad} className={primary} onClick={() => selected && void act('Scorecard snapshot recorded', () => api(`/api/afos360/v1/squads/${encodeURIComponent(selected)}/scorecard`, { method: 'POST', body: JSON.stringify({}) }))}>
                    Record scorecard
                  </button>
                  <button
                    type="button"
                    disabled={busy || loadingSquad || !scorecard.promotion.eligible}
                    className={secondary}
                    onClick={() => selected && void act('Promoted to super team', () => api(`/api/afos360/v1/teams/${encodeURIComponent(selected)}/promote-super`, { method: 'POST' }))}
                  >
                    Promote (named human)
                  </button>
                </div>
                {history.length > 0 && (
                  <div className="text-xs text-[var(--color-mist)]">
                    History: {history.slice(-5).map((h) => `${h.recorded_at?.slice(0, 16)} ${h.grade}/${h.score}`).join(' → ')}
                  </div>
                )}
              </div>
            </div>
          )}

          {skills && (
            <div className="space-y-1 rounded-2xl border border-[var(--color-line)] p-3 text-sm">
              <div className="flex items-center gap-2 font-semibold">
                <Wrench className="h-4 w-4" /> Skills · coverage {pct(skills.coverage)}
              </div>
              <div className="text-[var(--color-mist)]">
                unused bound: {skills.unused_bound.join(', ') || 'none'} · used unbound: {skills.unbound_used.join(', ') || 'none'} · missing from library:{' '}
                {skills.missing_from_library.join(', ') || 'none'}
              </div>
              <div className="grid gap-1 md:grid-cols-2">
                {skills.skills.map((s) => (
                  <div key={s.skill_key} className="flex justify-between rounded-xl border border-[var(--color-line)] px-2 py-1">
                    <span>
                      {s.skill_key} {s.bound ? '' : '(unbound)'}
                    </span>
                    <span className="text-[var(--color-mist)]">
                      {s.invocations}× · ok {pct(s.success_rate)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selected && (
            <div className="grid gap-4 md:grid-cols-2">
              <form className="space-y-2" onSubmit={onAdherence}>
                <div className="flex items-center gap-2 font-semibold">
                  <ShieldCheck className="h-4 w-4" /> Score adherence (0–1)
                </div>
                {adherenceDims.map((d) => (
                  <input key={d} className={field} placeholder={d} value={adherence[d] || ''} onChange={(e) => setAdherence({ ...adherence, [d]: e.target.value })} />
                ))}
                <button type="submit" disabled={busy || loadingSquad} className={secondary}>
                  Save adherence
                </button>
              </form>
              <form className="space-y-2" onSubmit={onCompliance}>
                <div className="font-semibold">Audit compliance</div>
                {complianceChecks.map((c) => (
                  <label key={c} className="flex items-center gap-2">
                    <input type="checkbox" checked={!!compliance[c]} onChange={(e) => setCompliance({ ...compliance, [c]: e.target.checked })} />
                    {c}
                  </label>
                ))}
                <button type="submit" disabled={busy || loadingSquad || !complianceLoaded} className={secondary}>
                  {complianceLoaded ? 'Save audit' : 'Checks not loaded'}
                </button>
              </form>
              <form className="space-y-2" onSubmit={onBind}>
                <div className="font-semibold">Bind skills (comma separated keys)</div>
                <input className={field} value={bindKeys} onChange={(e) => setBindKeys(e.target.value)} placeholder="evidence, test, build" />
                <button type="submit" disabled={busy || loadingSquad || !bindKeys.trim()} className={secondary}>
                  Bind
                </button>
              </form>
              <form className="space-y-2" onSubmit={onActivity}>
                <div className="flex items-center gap-2 font-semibold">
                  <Activity className="h-4 w-4" /> Record activity
                </div>
                <select className={field} value={activityKind} onChange={(e) => setActivityKind(e.target.value)}>
                  {ACTIVITY_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
                <input className={field} value={activitySummary} onChange={(e) => setActivitySummary(e.target.value)} placeholder="summary" />
                {needsBlocker && (
                  <input className={field} value={activityBlocker} onChange={(e) => setActivityBlocker(e.target.value)} placeholder="blocker id (required)" />
                )}
                {needsSkills && (
                  <input className={field} value={activitySkills} onChange={(e) => setActivitySkills(e.target.value)} placeholder="skill keys, comma separated (required)" />
                )}
                {needsCounterpart && (
                  <input className={field} value={activityCounterpart} onChange={(e) => setActivityCounterpart(e.target.value)} placeholder="counterpart team id TEAM_… (required)" />
                )}
                {(
                  <>
                    <input className={field} value={activityRun} onChange={(e) => setActivityRun(e.target.value)} placeholder="run id SDL_… (optional context)" />
                    <input className={field} value={activityStage} onChange={(e) => setActivityStage(e.target.value)} placeholder="stage id (optional context)" />
                  </>
                )}
                <button type="submit" disabled={busy || loadingSquad || !activityReady} className={secondary}>
                  Record
                </button>
              </form>
            </div>
          )}

          {activity.length > 0 && (
            <ol className="space-y-1 text-xs text-[var(--color-mist)]">
              {activity.map((row) => (
                <li key={row.activity_id}>
                  {row.at || '—'} · {row.kind}
                  {row.stage_id ? ` · ${row.stage_id}` : ''}
                  {row.run_id ? ` · ${row.run_id}` : ''}
                  {row.actor ? ` · ${row.actor}` : ''}
                  {row.summary ? ` · ${row.summary}` : ''}
                </li>
              ))}
            </ol>
          )}
        </section>
      </main>
    </AppChrome>
  )
}
